/* ============================================================================
 * useCRUDOperations.ts - CRUD operation wrapper functions
 * ============================================================================ */

import type { NodeUpdateOptions, OperationPersistenceOptions } from '../types';
import { validConditionalContentUpdate } from '@/shared/utils/conditionalNodeUpdate';
import { useCallback } from 'react';
import type { JsonLikeValue, NodeOperation, ProjectData, ProjectNode } from '@/shared/types/projectData';
import { normalizeTaskStateUpdate } from '@/features/projects/services/taskDetailsSync';
import { getNodeId, getProjectIdFromNodeId, isNamespacedId } from '@/features/projects/utils/projectUtils';
import { applyParentCreateDefaults, resolveParentCreateDefaults } from '@/shared/utils/createNodeDefaults';
import { resolveCreateOperationProjectId } from './utils/createOperationProjectId';
import { getAllDescendantIds } from '@/shared/utils/projectDataUtils';

interface UseCRUDOperationsParams {
  projectData: ProjectData | null;
  applyOp: (operation: NodeOperation, options?: OperationPersistenceOptions) => Promise<void>;
}

type NodeUpdatePayload = Partial<ProjectNode> & {
  client?: string;
  applyToDescendants?: boolean;
};

/**
 * Hook for CRUD operation wrappers
 */
export function useCRUDOperations({
  projectData,
  applyOp
}: UseCRUDOperationsParams) {
  const findCurrentNode = useCallback((nodeId: string) => {
    const exactNode = projectData?.nodes.find((node) => node.id === nodeId);
    if (exactNode) {
      return exactNode;
    }

    const rawNodeId = getNodeId(nodeId);
    const requestedProjectId = isNamespacedId(nodeId)
      ? getProjectIdFromNodeId(nodeId)
      : undefined;
    const belongsToRequestedProject = (node: ProjectNode) => {
      if (!requestedProjectId) {
        return true;
      }
      const nodeProjectId =
        (typeof node.projectId === 'string' && node.projectId !== 'default' ? node.projectId : undefined) ||
        (isNamespacedId(node.id) ? getProjectIdFromNodeId(node.id) : undefined) ||
        (projectData?.projectId !== 'default' ? projectData?.projectId : undefined);
      return nodeProjectId === requestedProjectId;
    };

    const rawExactNode = projectData?.nodes.find(
      (node) => node.id === rawNodeId && belongsToRequestedProject(node),
    );
    if (rawExactNode) {
      return rawExactNode;
    }

    const canonicalMatches = projectData?.nodes.filter(
      (node) => getNodeId(node.id) === rawNodeId && belongsToRequestedProject(node),
    ) || [];
    return canonicalMatches.length === 1 ? canonicalMatches[0] : undefined;
  }, [projectData?.nodes, projectData?.projectId]);

  const hasAmbiguousRawNodeId = useCallback((nodeId: string) => {
    if (isNamespacedId(nodeId) || projectData?.nodes.some((node) => node.id === nodeId)) {
      return false;
    }
    const rawNodeId = getNodeId(nodeId);
    return (projectData?.nodes.filter((node) => getNodeId(node.id) === rawNodeId).length || 0) > 1;
  }, [projectData?.nodes]);

  const hasForeignNamespaceCollision = useCallback((nodeId: string) => {
    if (!isNamespacedId(nodeId)) {
      return false;
    }
    const requestedProjectId = getProjectIdFromNodeId(nodeId);
    const rawNodeId = getNodeId(nodeId);
    return projectData?.nodes.some((node) => {
      if (getNodeId(node.id) !== rawNodeId) {
        return false;
      }
      const nodeProjectId =
        (typeof node.projectId === 'string' && node.projectId !== 'default' ? node.projectId : undefined) ||
        (isNamespacedId(node.id) ? getProjectIdFromNodeId(node.id) : undefined) ||
        (projectData?.projectId !== 'default' ? projectData?.projectId : undefined);
      return nodeProjectId !== requestedProjectId;
    }) || false;
  }, [projectData?.nodes, projectData?.projectId]);

  const getPreparedUpdates = useCallback((nodeId: string, updates: NodeUpdatePayload) => {
    const { client: _client, applyToDescendants: _applyToDescendants, ...filteredUpdates } = updates;
    const currentNode = findCurrentNode(nodeId);
    if (!currentNode && hasForeignNamespaceCollision(nodeId)) {
      throw new Error(`Unable to update ${nodeId}: its canonical ID is present in another project.`);
    }
    return normalizeTaskStateUpdate(filteredUpdates, currentNode);
  }, [findCurrentNode, hasForeignNamespaceCollision]);
  
  // Move node to target position
  const moveNodeTo = useCallback(async (
    nodeId: string,
    targetParentId: string | null,
    targetIndex: number,
    options?: {
      nodeIds?: string[];
      sourceParentId?: string | null;
      sourceNodeIds?: string[];
    }
  ) => {
    const currentNode = findCurrentNode(nodeId);
    const projectId =
      currentNode?.projectId ||
      getProjectIdFromNodeId(nodeId, projectData?.projectId || undefined) ||
      projectData?.projectId;
    const normalizedProjectId = projectId && projectId !== 'default'
      ? projectId
      : undefined;
    const operationNodeId = currentNode?.id || nodeId;

    await applyOp({
      type: 'move',
      nodeId: operationNodeId,
      targetParentId,
      targetIndex,
      ...(normalizedProjectId ? { metadata: { projectId: normalizedProjectId } } : {}),
      ...(options?.nodeIds ? { nodeIds: options.nodeIds } : {}),
      ...(options?.sourceParentId !== undefined ? { sourceParentId: options.sourceParentId } : {}),
      ...(options?.sourceNodeIds ? { sourceNodeIds: options.sourceNodeIds } : {}),
    });
  }, [applyOp, findCurrentNode, projectData?.projectId]);

  // Update node
  const updateNode = useCallback(async (nodeId: string, updates: NodeUpdatePayload, options?: NodeUpdateOptions) => {
    const conditional = !!options && Object.prototype.hasOwnProperty.call(options, 'expectedFields');
    if (conditional && !validConditionalContentUpdate(updates, options.expectedFields)) {
      throw new Error('Conditional updates require matching expectations for every content field.');
    }
    const currentNode = findCurrentNode(nodeId);
    if (!currentNode && hasForeignNamespaceCollision(nodeId)) {
      throw new Error(`Unable to update ${nodeId}: its canonical ID is present in another project.`);
    }
    if (!currentNode && hasAmbiguousRawNodeId(nodeId)) {
      throw new Error(`Unable to determine the target project for ambiguous node ID ${nodeId}.`);
    }
    if (
      typeof updates.projectId === 'string' && updates.projectId !== 'default' &&
      typeof currentNode?.projectId === 'string' && currentNode.projectId !== 'default' &&
      updates.projectId !== currentNode.projectId
    ) {
      throw new Error(`Update node ${nodeId} belongs to ${currentNode.projectId}, not explicit project ${updates.projectId}.`);
    }
    const projectId =
      updates.projectId ||
      currentNode?.projectId ||
      getProjectIdFromNodeId(nodeId, projectData?.projectId || undefined) ||
      projectData?.projectId;
    const normalizedProjectId = projectId && projectId !== 'default'
      ? projectId
      : undefined;
    const operationMetadata: Record<string, JsonLikeValue> = {};
    if (normalizedProjectId) {
      operationMetadata.projectId = normalizedProjectId;
    }
    if (typeof updates.applyToDescendants === 'boolean') {
      operationMetadata.applyToDescendants = updates.applyToDescendants;
    }

    const operation: NodeOperation = {
      type: 'update',
      nodeId: currentNode?.id || nodeId,
      // A content compare-and-set must not acquire lifecycle fields from a stale view.
      data: conditional ? structuredClone(updates) : getPreparedUpdates(nodeId, updates),
      ...(conditional ? { expectedFields: structuredClone(options.expectedFields) } : {}),
      ...(Object.keys(operationMetadata).length > 0 ? { metadata: operationMetadata } : {}),
    };
    if (conditional || options?.requireConfirmed) await applyOp(operation, { requireConfirmed: true });
    else await applyOp(operation);
  }, [applyOp, findCurrentNode, getPreparedUpdates, hasAmbiguousRawNodeId, hasForeignNamespaceCollision, projectData?.projectId]);

  // Batch update nodes
  const batchUpdateNodes = useCallback(async (updates: Array<{ nodeId: string; updates: Partial<ProjectNode> }>, options?: OperationPersistenceOptions) => {
    if (updates.length === 0) {
      if (options?.requireConfirmed) throw new Error('No updates to confirm');
      return;
    }

    const updatesByProject = new Map<string, Array<{ nodeId: string; data: Partial<ProjectNode> }>>();
    for (const { nodeId, updates: nodeUpdates } of updates) {
      const currentNode = findCurrentNode(nodeId);
      if (!currentNode && hasForeignNamespaceCollision(nodeId)) {
        throw new Error(`Unable to update ${nodeId}: its canonical ID is present in another project.`);
      }
      if (!currentNode && hasAmbiguousRawNodeId(nodeId)) {
        throw new Error(`Unable to determine the target project for ambiguous node ID ${nodeId}.`);
      }
      const explicitProjectId = typeof nodeUpdates.projectId === 'string' && nodeUpdates.projectId !== 'default'
        ? nodeUpdates.projectId
        : undefined;
      const nodeProjectId = typeof currentNode?.projectId === 'string' && currentNode.projectId !== 'default'
        ? currentNode.projectId
        : undefined;
      const namespacedProjectId = isNamespacedId(nodeId)
        ? getProjectIdFromNodeId(nodeId)
        : undefined;
      const activeProjectId = projectData?.projectId && projectData.projectId !== 'default'
        ? projectData.projectId
        : undefined;

      if (explicitProjectId && nodeProjectId && explicitProjectId !== nodeProjectId) {
        throw new Error(`Batch update node ${nodeId} belongs to ${nodeProjectId}, not explicit project ${explicitProjectId}.`);
      }

      const projectId = explicitProjectId || nodeProjectId || namespacedProjectId || activeProjectId;

      if (!projectId) {
        throw new Error(`Unable to determine the target project for batch update node ${nodeId}.`);
      }

      const projectUpdates = updatesByProject.get(projectId) || [];
      projectUpdates.push({
        nodeId: currentNode?.id || nodeId,
        data: getPreparedUpdates(nodeId, nodeUpdates),
      });
      updatesByProject.set(projectId, projectUpdates);
    }

    for (const [projectId, projectUpdates] of updatesByProject) {
      const operation: NodeOperation = {
        type: 'batch-update',
        updates: projectUpdates,
        metadata: { projectId, atomic: true },
      };
      if (options) await applyOp(operation, options);
      else await applyOp(operation);
    }
  }, [applyOp, findCurrentNode, getPreparedUpdates, hasAmbiguousRawNodeId, hasForeignNamespaceCollision, projectData?.projectId]);

  // Move group
  const moveGroup = useCallback(async (groupId: string, daysDelta: number, projectIdHint?: string) => {
    const currentGroup = findCurrentNode(groupId);
    const projectId =
      projectIdHint ||
      currentGroup?.projectId ||
      getProjectIdFromNodeId(groupId, projectData?.projectId || undefined) ||
      projectData?.projectId;
    const normalizedProjectId = projectId && projectId !== 'default'
      ? projectId
      : undefined;
    // Preserve the current tree's ID shape so the optimistic group-move reducer
    // can shift the in-memory group and descendant dates immediately. The
    // server path still gets the authoritative project via metadata and strips
    // any namespace when building the wire operation.
    const operationNodeId = currentGroup?.id || groupId;
    await applyOp({
      type: 'group-move',
      nodeId: operationNodeId,
      daysDelta,
      ...(normalizedProjectId ? { metadata: { projectId: normalizedProjectId } } : {}),
    });
  }, [applyOp, findCurrentNode, projectData?.projectId]);

  // Delete node
  const deleteNode = useCallback(async (nodeId: string) => {
    const currentNode = findCurrentNode(nodeId);
    if (!currentNode && hasForeignNamespaceCollision(nodeId)) {
      throw new Error(`Unable to delete ${nodeId}: its canonical ID is present in another project.`);
    }
    if (!currentNode && hasAmbiguousRawNodeId(nodeId)) {
      throw new Error(`Unable to determine the target project for ambiguous node ID ${nodeId}.`);
    }
    const projectId =
      currentNode?.projectId ||
      getProjectIdFromNodeId(nodeId, projectData?.projectId || undefined);
    const normalizedProjectId = projectId && projectId !== 'default'
      ? projectId
      : (projectData?.projectId || undefined);

    await applyOp({
      type: currentNode?.type === 'group' ? 'delete-tree' : 'delete',
      nodeId: currentNode?.id || nodeId,
      ...(normalizedProjectId ? { metadata: { projectId: normalizedProjectId } } : {}),
    });
  }, [applyOp, findCurrentNode, hasAmbiguousRawNodeId, hasForeignNamespaceCollision, projectData?.projectId]);
  
  // Delete multiple nodes
  const deleteNodes = useCallback(async (nodeIds: string[]) => {
    if (nodeIds.length === 0) return;

    const groupedNodeIds = new Map<string, string[]>();
    nodeIds.forEach((nodeId) => {
      const currentNode = findCurrentNode(nodeId);
      if (!currentNode && hasForeignNamespaceCollision(nodeId)) {
        throw new Error(`Unable to delete ${nodeId}: its canonical ID is present in another project.`);
      }
      if (!currentNode && hasAmbiguousRawNodeId(nodeId)) {
        throw new Error(`Unable to determine the target project for ambiguous node ID ${nodeId}.`);
      }
      const projectId =
        currentNode?.projectId ||
        getProjectIdFromNodeId(nodeId, projectData?.projectId || undefined);
      const normalizedProjectId = projectId && projectId !== 'default'
        ? projectId
        : (projectData?.projectId || 'default');
      const existing = groupedNodeIds.get(normalizedProjectId) || [];
      existing.push(currentNode?.id || nodeId);
      groupedNodeIds.set(normalizedProjectId, existing);
    });

    for (const [projectId, ids] of groupedNodeIds.entries()) {
      const uniqueIds = [...new Set(ids)];
      const roots = uniqueIds.filter(id => !projectData || !uniqueIds.some(parent => parent !== id &&
        getAllDescendantIds(projectData, parent).includes(id)));
      const leafIds: string[] = [];
      for (const id of roots) {
        if (findCurrentNode(id)?.type === 'group') {
          // Let the server resolve the current subtree, including new children.
          await applyOp({ type: 'delete-tree', nodeId: id, metadata: { projectId } });
        } else {
          leafIds.push(id);
        }
      }
      if (leafIds.length === 0) continue;
      await applyOp({
        type: 'batch-delete',
        nodeIds: leafIds,
        metadata: {
          projectId,
        },
      });
    }

  }, [applyOp, findCurrentNode, hasAmbiguousRawNodeId, hasForeignNamespaceCollision, projectData]);

  // Create node
  const createNode = useCallback(async (node: NodeUpdatePayload, parentId?: string, index?: number) => {
    // Filter out client field - client is managed at project level only
    const { client: _client, ...filteredNode } = node;
    const effectiveParentId = parentId ?? filteredNode.parentId;
    const parentDefaults = resolveParentCreateDefaults(
      projectData?.nodes || [],
      effectiveParentId,
    );
    const preparedNode = applyParentCreateDefaults(
      normalizeTaskStateUpdate(filteredNode, filteredNode),
      parentDefaults,
    );
    const explicitProjectId =
      typeof preparedNode.projectId === 'string' && preparedNode.projectId !== 'default'
        ? preparedNode.projectId
        : undefined;
    const hasMultipleVisibleProjects = (() => {
      try {
        const visibleProjectIds = (window as unknown as Record<string, Record<string, unknown>>).__multiProjectContext?.visibleProjectIds;
        return Array.isArray(visibleProjectIds) && visibleProjectIds.filter((id: unknown) => typeof id === 'string' && id && id !== 'default').length > 1;
      } catch {
        return false;
      }
    })();
    const projectId = resolveCreateOperationProjectId({
      explicitProjectId,
      parentId: effectiveParentId,
      activeProjectId: projectData?.projectId,
      hasMultipleVisibleProjects,
    });

    if (!projectId) {
      throw new Error('Unable to determine the target project for this task. Select a specific project and try again.');
    }

    await applyOp({
      type: 'create',
      data: preparedNode,
      parentId: effectiveParentId,
      targetIndex: index,
      ...(projectId ? { metadata: { projectId } } : {}),
    });
  }, [applyOp, projectData?.nodes, projectData?.projectId]);
  
  // Atomic progress update
  const updateProgress = useCallback(async (nodeId: string, progress: number) => {
    const currentNode = findCurrentNode(nodeId);
    const projectId =
      currentNode?.projectId ||
      getProjectIdFromNodeId(nodeId, projectData?.projectId || undefined) ||
      projectData?.projectId;

    await applyOp({
      type: 'update',
      nodeId,
      data: getPreparedUpdates(nodeId, { progress }),
      ...(projectId ? { metadata: { projectId } } : {}),
    });
  }, [applyOp, findCurrentNode, getPreparedUpdates, projectData?.projectId]);
  
  return {
    moveNodeTo,
    updateNode,
    batchUpdateNodes,
    moveGroup,
    deleteNode,
    deleteNodes,
    createNode,
    updateProgress
  };
}
